"use client";

import { useMedia } from "@/hooks/system/useMedia";
import S from "./index.module.scss";
import { FC, useMemo } from "react";
import CustomIcon from "@/components/ui/custom-icon";

type DocType = {
  name: string;
  link: string;
  extension: string;
};

interface IRestDocs {
  docs: DocType[];
}

const DesktopGrid: FC<IRestDocs> = ({ docs }) => {
  const columns = Math.min(4, Math.max(1, docs.length));

  const renderDoc = (item: DocType, idx: number) => (
    <div className={S.docItem} key={idx} style={{ borderRight: (idx + 1) % columns === 0 ? "none" : undefined }}>
      {item.name && <span className={S.name}>{item.name}</span>}
      <div className={S.bottomWrapper}>
        {item.extension && <span className={S.extension}>{item.extension}</span>}
        {item.link && (
          <a
            className={S.downloadBtn}
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={item.name}
          >
            <CustomIcon fileName={"icon-download"} />
          </a>
        )}
      </div>
    </div>
  );

  const fullDocsGrid: DocType[] = useMemo(() => {
    const leftInGrid = (columns - (docs.length % columns)) % columns;
    const emptyDocs = Array.from({ length: leftInGrid }, () => ({
      name: "",
      link: "",
      extension: "",
    }));

    return [...docs, ...emptyDocs];
  }, [docs, columns]);

  return (
    <div className={S.docsGridBorder}>
      <div className={S.docsGrid} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {fullDocsGrid.map((doc, idx) => renderDoc(doc, idx))}
      </div>
    </div>
  );
};

const MobileList: FC<IRestDocs> = ({ docs }) => {
  const renderDoc = (item: DocType, id: number) => (
    <div className={S.docItemMobile} key={id}>
      <div className={S.textWrap}>
        <span className={S.name}>{item.name}</span>
        <span className={S.extension}>{item.extension}</span>
      </div>
      <a className={S.downloadBtn} href={item.link} target="_blank" rel="noopener noreferrer" aria-label={item.name}>
        <CustomIcon fileName={"icon-download"} />
      </a>
    </div>
  );

  return <div className={S.docsList}>{docs.map((doc, idx) => renderDoc(doc, idx))}</div>;
};

const RestDocs: FC<IRestDocs> = ({ docs }) => {
  const { isClient, isDesktop } = useMedia();

  return isClient && isDesktop ? <DesktopGrid docs={docs} /> : <MobileList docs={docs} />;
};

export default RestDocs;
